import { Link } from 'react-router-dom';
import { X } from 'lucide-react';
import { Badge } from '@/components/ui/Badge';
import {
  relativeTime,
  severityLabel,
  useDismiss,
  useMarkRead,
  type NotificationRow,
} from '@/lib/notifications';
import { cn } from '@/lib/cn';

type Tone = 'neutral' | 'blue' | 'yellow' | 'red';

function toneForSeverity(severity: string): Tone {
  switch (severity) {
    case 'critical':
      return 'red';
    case 'action_required':
      return 'yellow';
    case 'warning':
      return 'yellow';
    default:
      return 'blue';
  }
}

export interface NotificationItemProps {
  row: NotificationRow;
  onActivate?: () => void;
  compact?: boolean;
}

export function NotificationItem({ row, onActivate, compact }: NotificationItemProps) {
  const markRead = useMarkRead();
  const dismiss = useDismiss();
  const unread = row.read_at == null;

  const handleActivate = () => {
    if (unread) markRead.mutate(row.id);
    onActivate?.();
  };

  const handleDismiss = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dismiss.mutate(row.id);
  };

  const content = (
    <div
      className={cn(
        'group relative flex gap-3 px-4 py-3',
        compact ? 'py-2.5' : 'py-3',
        unread ? 'bg-brand-50/30' : 'bg-transparent',
        'hover:bg-surface-sunk transition-colors'
      )}
    >
      <div className="mt-0.5 flex-shrink-0">
        <span
          className={cn(
            'block h-2 w-2 rounded-full',
            unread ? 'bg-brand-500' : 'bg-transparent border border-rule'
          )}
          aria-hidden="true"
        />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <Badge tone={toneForSeverity(row.severity)}>{severityLabel(row.severity)}</Badge>
          <span className="text-[0.6875rem] text-ink-muted">{relativeTime(row.created_at)}</span>
        </div>
        <div
          className={cn(
            'mt-1 text-[0.875rem] leading-snug',
            unread ? 'font-semibold text-ink' : 'text-ink'
          )}
        >
          {row.title}
        </div>
        {row.body && !compact && (
          <div className="mt-1 text-[0.8125rem] text-ink-muted leading-snug line-clamp-2">
            {row.body}
          </div>
        )}
      </div>
      <button
        type="button"
        onClick={handleDismiss}
        aria-label="Dismiss notification"
        className="flex-shrink-0 h-7 w-7 grid place-items-center rounded-md text-ink-muted opacity-0 group-hover:opacity-100 hover:bg-surface-sunk hover:text-ink focus:opacity-100 transition-opacity"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );

  if (row.link) {
    return (
      <Link to={row.link} onClick={handleActivate} className="block">
        {content}
      </Link>
    );
  }
  return (
    <button type="button" onClick={handleActivate} className="block w-full text-left">
      {content}
    </button>
  );
}
